import Box from '@/common/components/Box'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import ResumeItem from '@/common/components/ResumeItem'
import { formatUnit } from '@/common/utils/formatters'
import { DiagnosticActivityData } from '@/features/maintenance/features/workflows/types/forwardings/directiveEntries/diagnosticEntry/diagnosticEntry.type'
import { WrenchIcon } from 'lucide-react'
import EditProposalModal from '../../EditProposalModal'
import ProposalAwareValuesRow from '../../ProposalAwareValuesRow'
import { useProposalModification } from '../../useProposalModification'

interface ActivityRowProps {
  executionActivity: DiagnosticActivityData
  fieldIndex: number
}

const ActivityRow = ({ executionActivity, fieldIndex }: ActivityRowProps) => {
  const modification = useProposalModification({
    fieldName: `activities.${fieldIndex}`,
    unitPrice: executionActivity.unit_price,
    warrantyInDays: executionActivity.warranty_in_days,
    quantity: executionActivity.quantity,
    totalValue: executionActivity.total_value,
  })

  const quantityDisplay =
    executionActivity.unit_for_quantity === 'minutes' ? (
      <ResumeItem
        className="text-sm"
        label="Tempo de execução"
        value={formatUnit(executionActivity.quantity, 'hours')}
      />
    ) : (
      <ResumeItem className="text-sm" label="Unidades" value={formatUnit(executionActivity.quantity, 'item')} />
    )

  return (
    <Box className="p-4">
      <div className="flex w-full items-start gap-4">
        <ImageCarousel
          files={
            executionActivity.images?.map((image) => ({
              id: image.id,
              file: image?.url,
            })) || []
          }
          Placeholder={
            <ImageWithPlaceholder className="size-28 rounded-md" iconClassName="size-10" placeholderIcon={WrenchIcon} />
          }
          className="size-28"
          showExpandButton
          showRemoveButton={false}
        />

        <div className="flex flex-col gap-2 flex-1">
          <p className="font-bold -mb-2">{executionActivity?.display_name || '--'}</p>

          <div className="flex flex-col gap-3">
            <ProposalAwareValuesRow
              originalUnitPrice={modification.originalUnitPrice}
              displayNewUnitPrice={modification.displayNewUnitPrice}
              unitPricePercentage={modification.unitPricePercentage}
              unitPriceLabel={executionActivity.unit_for_quantity === 'minutes' ? 'Valor por hora' : 'Valor unitário'}
              originalTotalValue={modification.originalTotalValue}
              displayNewTotalValue={modification.displayNewTotalValue}
              totalValuePercentage={modification.totalValuePercentage}
              originalWarranty={modification.originalWarranty}
              displayNewWarranty={modification.displayNewWarranty}
              quantityDisplay={quantityDisplay}
            />
          </div>

          <ResumeItem className="text-sm" label="Descrição" value={executionActivity.description} />
        </div>

        <EditProposalModal
          title={`Editar ${executionActivity?.display_name || '--'}`}
          isOpen={modification.isOpen}
          setIsOpen={modification.handleOpenChange}
          onConfirm={modification.onConfirm}
          localUnitPrice={modification.localUnitPrice}
          setLocalUnitPrice={modification.setLocalUnitPrice}
          localWarranty={modification.localWarranty}
          setLocalWarranty={modification.setLocalWarranty}
          originalUnitPrice={modification.originalUnitPrice}
          originalTotalValue={modification.originalTotalValue}
          originalWarranty={modification.originalWarranty}
          previewTotalValue={modification.previewTotalValue}
          unitPriceLabel={executionActivity.unit_for_quantity === 'minutes' ? 'Valor por hora' : 'Valor unitário'}
          quantityDisplay={quantityDisplay}
          disabled={modification.disabled}
        />
      </div>
    </Box>
  )
}

export default ActivityRow
